import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { phCapture } from '@/lib/analytics/posthog';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';
import { DreiSchritte } from '../components/personen/DreiSchritte';
import { GeschichtenschrittBlock } from '../components/geschichtenschritt/GeschichtenschrittBlock';
import { K2ConfirmDialog } from '../components/personen/K2ConfirmDialog';
import { LightGateOverlay } from '../components/personen/LightGateOverlay';
import { PersonenFlow } from '../components/personen/PersonenFlow';
import { SchrittBlock } from '../components/personen/SchrittBlock';
import type { SchrittBlockState } from '../components/personen/SchrittBlock';
import type { CastPhotoFiles } from '../components/personen/LightGateOverlay';
import { CharacterCounter } from '../components/geschichte/CharacterCounter';
import {
  TITELWUNSCH_MAX_LENGTH,
  useBookConfiguratorStore,
  useLocalDraftRedirectGuard,
} from '../state/book-configurator-store';
import type { BlockIndex, BlockNavState, CastTextEntry } from '../state/book-configurator-store';
import { allPersonsComplete } from '../components/personen/is-person-complete';
import {
  clearAllPhotoPicks,
  clearPhotoPicksFor as idbClearPhotoPicksFor,
  getAllPhotoPicks,
  putPhotoPick,
} from '../state/photo-picks-idb';
import { needsHeicPreviewConversion, previewHeicInBrowser } from '../photo-preview-heic';
import { captureReadableFailure, isFileReadable } from '../photo-materialize';

/**
 * 5a · Schritt 1 · Personen orchestrator — one-question-at-a-time
 * (Sebastian ruling 2026-09-24). Block 1's giant orchestrator was
 * replaced with a `PersonenFlow` child that owns the per-entry step
 * machine; PersonenRoute is thin now — it still owns:
 *
 *   1. Case-1 mount redirect (pendingBookOrderId)
 *   2. Book-funnel analytics (book_builder_opened + recipient_chosen
 *      + first_person_saved + name_entered + person_completed +
 *      first_photo_step_reached + photo_uploaded + light_gate_shown +
 *      light_gate_completed)
 *   3. IDB hydration + HEIC preview machinery for photos (unchanged)
 *   4. The K2ConfirmDialog invocation (flow signals `onFacePickedForK2`
 *      → parent sets `k2Key` → K2 opens; flow watches `k2OpenKey`
 *      transition to null to advance the entry to `complete`)
 *   5. Block 2 (Freitext) + Block 3 (Titelwunsch) + terminal CTA +
 *      LightGateOverlay integration (unchanged from 2026-08-31 evening)
 *
 * Blocks numbering unchanged: Personen = 1, Freitext = 2, Titelwunsch
 * = 3. Block 1 SchrittBlock hides its "1." ordinal (hideNumber).
 */

/**
 * Sebastian ruling 2026-09-23 (final) — HEIC-aware slot status. See
 * FotoUploadSlot.tsx for the full four-state semantics.
 */
type PhotoStatus = 'ready' | 'converting' | 'heic';

interface PhotoEntry {
  readonly file: File;
  readonly url: string | null;
  readonly status: PhotoStatus;
}

interface CastPhotos {
  readonly face: PhotoEntry | null;
}

type PhotoMap = ReadonlyMap<string, CastPhotos>;

const EMPTY_PHOTOS: CastPhotos = { face: null };

const TERMINAL_BLOCK: BlockIndex = 3;
const DEFAULT_INCOMPLETE_NAV: BlockNavState = { reached: [1], openBlock: 1 };

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const BUTTON_OUTLINE_SM =
  'h-11 rounded-lg border-[1.5px] border-dream-navy bg-dream-white px-4 text-sm font-semibold text-dream-navy disabled:opacity-50';
const FIELD_CLASS = 'builder-field h-11 w-full px-3 text-base text-dream-navy outline-none';

/**
 * Book-funnel PostHog helper — fires a phCapture event once per scope
 * per browser session/orderId. `phCapture` returns `false` when
 * PostHog is not yet initialised (statistics consent not granted), so
 * the sentinel is only written on a real emission — the event will
 * fire later when the customer consents.
 */
function fireBookAnalytics(
  event: string,
  dedupKey: string,
  storage: 'session' | 'local',
  properties?: Record<string, unknown>,
): void {
  try {
    const store = storage === 'session' ? window.sessionStorage : window.localStorage;
    if (store.getItem(dedupKey) === '1') return;
    if (phCapture(event, properties)) {
      store.setItem(dedupKey, '1');
    }
  } catch {
    // storage access failures + phCapture errors are non-fatal
  }
}

/**
 * Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — decide which
 * block to open on mount.
 *
 * Rules, in order:
 *
 *  (1) PERSISTED nav exists → VALIDATE against current data (rule 2).
 *      If the stored openBlock is beyond block 1 but a person is now
 *      incomplete (e.g. photo was removed between sessions), fall
 *      back to `{reached: [1], openBlock: 1}`. If the stored
 *      openBlock is legal, trust the full stored shape.
 *
 *  (2) NO persisted nav (migrated draft, fresh seed, after reset) →
 *      DERIVE (rule 3). All persons complete → open the STORY step
 *      (block 2). Never auto-skip past the story based on `freitext`
 *      being non-empty — the customer may want to re-read / edit it.
 *      Persons incomplete → block 1 open.
 *
 * Pure function — call sites pass in the derived "allComplete" so
 * the predicate stays in one place (`is-person-complete.ts`).
 */
export function deriveBlockNavOnMount(input: {
  readonly persisted: BlockNavState | null;
  readonly allComplete: boolean;
}): BlockNavState {
  const { persisted, allComplete } = input;
  if (persisted !== null) {
    // VALIDATE: if the stored openBlock is beyond block 1 but a
    // person is now incomplete, fall back to block 1. Otherwise
    // trust the stored shape verbatim.
    const stored = persisted.openBlock;
    if (stored !== null && stored > 1 && !allComplete) {
      return DEFAULT_INCOMPLETE_NAV;
    }
    // Also catch a stored "no block open" (terminal CTA position)
    // while a person is incomplete — that would hide the forward
    // way completely.
    if (stored === null && !allComplete) {
      return DEFAULT_INCOMPLETE_NAV;
    }
    return persisted;
  }
  // DERIVE from scratch.
  if (allComplete) {
    return { reached: [1, 2], openBlock: 2 };
  }
  return DEFAULT_INCOMPLETE_NAV;
}

export function PersonenRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const [fromNewBookIntentAtMount] = useState(
    () => (location.state as { fromNewBookIntent?: boolean } | null)?.fromNewBookIntent === true,
  );
  const cast = useBookConfiguratorStore((s) => s.cast);
  const freitext = useBookConfiguratorStore((s) => s.freitext);
  const sprache = useBookConfiguratorStore((s) => s.sprache);
  const titelwunsch = useBookConfiguratorStore((s) => s.titelwunsch);
  // Geschichtenschritt prototype — storyKind threaded through to the
  // LightGate payload so the create-order DTO carries it (backend
  // persists intake.raw.storyKind; pipeline unaffected).
  const storyKind = useBookConfiguratorStore((s) => s.storyKind);
  const addCast = useBookConfiguratorStore((s) => s.addCast);
  const updateCast = useBookConfiguratorStore((s) => s.updateCast);
  const removeCast = useBookConfiguratorStore((s) => s.removeCast);
  const setFreitext = useBookConfiguratorStore((s) => s.setFreitext);
  const setTitelwunsch = useBookConfiguratorStore((s) => s.setTitelwunsch);
  const resetStore = useBookConfiguratorStore((s) => s.reset);
  const [lightGateOpen, setLightGateOpen] = useState(false);
  const [titelwunschError, setTitelwunschError] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);

  // K2 dialog is opened by the flow signalling `onFacePickedForK2`;
  // Sebastian ruling 2026-09-18 K2 fires on face-photo upload.
  const [k2Key, setK2Key] = useState<string | null>(null);

  // Sebastian ruling 2026-09-24 — the X in the corner, ESC, and the
  // outside-click behave like "Anderes Foto wählen": discard the
  // picked photo and return the customer to Q_photo. Only "Passt so"
  // confirms. Pre-2026-09-24 the X silently closed the dialog
  // preserving the photo — indistinguishable from confirming, which
  // trapped customers who tapped X thinking it was "back". This ref
  // lets `onOpenChange` distinguish the confirm path (where
  // `onConfirm` sets `k2ConfirmedRef.current=true` before flipping
  // k2Key) from the dismiss paths (X/ESC/outside), so a confirm
  // does NOT wipe the photo it just accepted.
  const k2ConfirmedRef = useRef(false);

  // W28 Option B (Sebastian ruling 2026-10-12 + 2026-10-13 cold-boot
  // reinforcement). `ready` gates the Case-1 mount redirect on
  // persist hydration — never navigate while the local draft is
  // still being read from localStorage. `hasContent` is a REACTIVE
  // subscription (not a one-shot `getState()`), so a late-arriving
  // cast-name update between first paint and effect firing still
  // flips the redirect off. Both values join the effect's deps so
  // the effect re-evaluates when either changes.
  const { ready: configuratorReady, hasContent: localDraftHasContentValue } =
    useLocalDraftRedirectGuard();

  // ─── Case-1 mount redirect ───────────────────────────────────────
  useEffect(() => {
    if (fromNewBookIntentAtMount) {
      if ((location.state as { fromNewBookIntent?: boolean } | null)?.fromNewBookIntent === true) {
        navigate(location.pathname, { replace: true, state: null });
      }
      return;
    }
    if (lightGateOpen) return;
    if (!configuratorReady) return;
    if (user && user.pendingBookOrderId !== null) {
      if (localDraftHasContentValue) return;
      navigate(`/book/${user.pendingBookOrderId}`, { replace: true });
    }
  }, [
    user,
    navigate,
    lightGateOpen,
    fromNewBookIntentAtMount,
    location.pathname,
    location.state,
    configuratorReady,
    localDraftHasContentValue,
  ]);

  // ─── Book-funnel analytics ───────────────────────────────────────
  //
  // `book_builder_opened` (Sebastian ruling 2026-10-08, rename of the
  // misleading pre-fix `book_order_started`). Fires once per session
  // on PersonenRoute mount — the entry point of the configurator. The
  // name no longer promises a customer commitment; it means "/book
  // landing committed in this tab". The real-start event is
  // `book_recipient_chosen`, fired from the first role pick.
  //
  // `entry` discriminates fresh builds from resumes. Cold = no pending
  // order on the user record (anonymous or signed-in with no draft);
  // resume = user has a `pendingBookOrderId`, which triggers the mount
  // redirect above — the event is captured BEFORE the redirect
  // navigation so the funnel sees it either way. Session-scoped
  // sessionStorage dedup so a tab reload inside the same session does
  // not re-fire.
  useEffect(() => {
    const entry: 'cold' | 'resume' = user?.pendingBookOrderId ? 'resume' : 'cold';
    fireBookAnalytics('book_builder_opened', 'book_builder_opened_fired', 'session', { entry });
    // Deps: user?.pendingBookOrderId drives the entry discriminator.
    // On a late user-hydration flip from null → user, the sentinel
    // ensures we do not re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sebastian ruling 2026-09-24 — first-person-saved fires when the
  // flow signals a cast entry transitioned to `complete`. Session-
  // scoped dedup via sessionStorage so the customer cycling in/out of
  // the route inside a single session fires ONCE.
  const handleFirstPersonComplete = useCallback(() => {
    fireBookAnalytics('book_first_person_saved', 'book_first_person_saved_fired', 'session', {
      cast_size: cast.length,
    });
  }, [cast.length]);

  // Sebastian ruling 2026-09-24 — first-photo-step-reached fires when
  // ANY cast entry first reaches the photo step. Session-scoped dedup.
  const handleFirstPhotoStepReached = useCallback(() => {
    fireBookAnalytics(
      'book_first_photo_step_reached',
      'book_first_photo_step_reached_fired',
      'session',
    );
  }, []);

  // ─── Per-entry analytics (Sebastian ruling 2026-10-08) ───────────
  //
  // Three per-entry funnel events wired via useRef-backed Set dedup
  // keyed on the stable castKey. Session-scoped (refs reset on route
  // unmount/remount; sessionStorage dedup separately handles the
  // tab-level invariant for events the parent chooses to use it on).
  // Zero PII in properties — cast_index + enums + ids only.
  const recipientChosenKeysRef = useRef<Set<string>>(new Set());
  const nameEnteredKeysRef = useRef<Set<string>>(new Set());
  const personCompletedKeysRef = useRef<Set<string>>(new Set());
  const photoUploadedKeysRef = useRef<Set<string>>(new Set());

  const handleRecipientChosen = useCallback(
    (castKey: string, role: 'kind' | 'erwachsener' | 'tier', castIndex: number) => {
      if (recipientChosenKeysRef.current.has(castKey)) return;
      recipientChosenKeysRef.current.add(castKey);
      phCapture('book_recipient_chosen', { role, cast_index: castIndex });
    },
    [],
  );

  const handlePersonNameEntered = useCallback((castKey: string, castIndex: number) => {
    if (nameEnteredKeysRef.current.has(castKey)) return;
    nameEnteredKeysRef.current.add(castKey);
    phCapture('book_person_name_entered', { cast_index: castIndex });
  }, []);

  const handlePersonCompleted = useCallback(
    (castKey: string, castIndex: number, castSize: number) => {
      if (personCompletedKeysRef.current.has(castKey)) return;
      personCompletedKeysRef.current.add(castKey);
      phCapture('book_person_completed', { cast_index: castIndex, cast_size: castSize });
    },
    [],
  );

  /**
   * Fires `book_photo_uploaded` once per (castKey, which) pair when
   * the customer picks a non-null file. Called synchronously from
   * `setSlotPhoto` below, BEFORE any IDB / HEIC side effects — so
   * even if preview conversion later fails, the funnel moment is
   * captured. Clearing a photo (file === null) is a no-op; swapping
   * to a new photo on an already-fired slot is also a no-op
   * (first-pick wins per slot).
   */
  const noteSlotPhotoForAnalytics = useCallback(
    (castKey: string, which: 'face', file: File | null) => {
      if (file === null) return;
      const sentinel = `${castKey}:${which}`;
      if (photoUploadedKeysRef.current.has(sentinel)) return;
      photoUploadedKeysRef.current.add(sentinel);
      const castIndex = cast.findIndex((c) => c.key === castKey);
      if (castIndex < 0) return;
      phCapture('book_photo_uploaded', { which, cast_index: castIndex });
    },
    [cast],
  );

  // Geschichtenschritt analytics (feat/story-step-golive, Sebastian
  // ruling 2026-10-09). Two events via the existing consent-guarded
  // `fireBookAnalytics` helper, deduped session-wide per unique value
  // so a flip from erlebt → erfunden records both commitments, but
  // re-ticking the same choice does not spam. No free text, no names
  // in properties — the enum / slug is all we send.
  const handleStoryKindChosen = useCallback((kind: 'erlebt' | 'erfunden') => {
    fireBookAnalytics('book_story_kind_chosen', `book_story_kind_chosen_${kind}_fired`, 'session', {
      kind,
    });
  }, []);

  const handleErfundenTileChosen = useCallback((tile: string) => {
    fireBookAnalytics('book_story_tile_chosen', `book_story_tile_chosen_${tile}_fired`, 'session', {
      tile,
    });
  }, []);

  useEffect(() => {
    if (!lightGateOpen) return;
    fireBookAnalytics('book_light_gate_shown', 'book_light_gate_shown_fired', 'session', {
      cast_size: cast.length,
      has_titelwunsch: titelwunsch.trim().length > 0,
    });
  }, [lightGateOpen, cast.length, titelwunsch]);

  // ─── Block-visibility state ──────────────────────────────────────
  //
  // Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — the
  // {reached, openBlock} pair is PERSISTED in the configurator store
  // so a reload restores the customer to the exact block they were
  // on. See `deriveBlockNavOnMount` above for the derive-fallback
  // (existing drafts) and validate-against-data rules.
  //
  // Reading from the store directly ensures writes (via setBlockNav)
  // round-trip across reloads. The one-time initial decision is
  // taken in a `useEffect` guarded on `photosHydrated` so a person
  // whose face photo is sitting in IDB is not misclassified as
  // "incomplete" during the hydration window.
  const blockNav = useBookConfiguratorStore((s) => s.blockNav);
  const setBlockNav = useBookConfiguratorStore((s) => s.setBlockNav);

  // ─── Photo state (unchanged from pre-2026-09-24) ─────────────────
  const [photos, setPhotos] = useState<PhotoMap>(new Map());
  // Reload-trap fix — flips true once the IDB hydration useEffect
  // has finished (whether it found rows or not). Blocks render until
  // this is true to avoid the "every person looks incomplete for a
  // frame" flash.
  const [photosHydrated, setPhotosHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getAllPhotoPicks();
        if (cancelled) return;
        if (rows.length === 0) {
          // Reload-trap fix: still flip the hydrated flag so the
          // block-nav decision can proceed. Empty IDB is a valid
          // post-hydration state (fresh customer, pre-first-photo).
          setPhotosHydrated(true);
          return;
        }
        const hydrated = new Map<string, CastPhotos>();
        const heicJobs: Array<{ castKey: string; which: 'face'; file: File }> = [];
        for (const row of rows) {
          // Legacy-row scrub (fix/single-photo). Fire-and-forget
          // BEFORE hydrateSlot so a hydrate-level error (e.g. a
          // malformed legacy File in jsdom) can't block the stale-
          // key cleanup. A pre-fix row may still carry a
          // `fullBody?: File` key from the removed second-photo
          // slot; structured-clone persistence preserves the
          // property even after the TypeScript shape narrows.
          // `putPhotoPick(_, 'face', _)` rewrites the row with the
          // face-only shape, dropping the stale key.
          if (hasLegacyFullBody(row)) {
            void putPhotoPick(row.castKey, 'face', row.face ?? null);
          }
          const face = await hydrateSlot(row.castKey, 'face', row.face ?? null, heicJobs);
          if (cancelled) return;
          // Purge the stale IDB row when the stored file was non-null
          // but failed the readability check — leaving it would make
          // the row re-appear incomplete on every reload while the
          // builder keeps re-asking for the photo. Fire-and-forget.
          if (row.face !== undefined && row.face !== null && face === null) {
            void putPhotoPick(row.castKey, 'face', null);
          }
          hydrated.set(row.castKey, { face });
        }
        setPhotos(hydrated);
        setPhotosHydrated(true);
        for (const job of heicJobs) {
          if (cancelled) return;
          void resolveHeicPreview(job.castKey, job.which, job.file);
        }
      } catch (err) {
        console.warn('[personen] hydrate photos from IDB failed — continuing in-memory', err);
        // Reload-trap fix: on IDB failure, flip the hydrated flag
        // anyway so the derive-fallback can run with whatever
        // photos are in-memory (empty on first mount; non-empty if
        // the customer pre-picked earlier in the same session).
        if (!cancelled) setPhotosHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveHeicPreview = useCallback(
    async (castKey: string, which: 'face', file: File): Promise<void> => {
      const outcome = await previewHeicInBrowser(file);
      setPhotos((prev) => {
        const existing = prev.get(castKey)?.[which];
        if (!existing || existing.file !== file) return prev;
        if (existing.url) URL.revokeObjectURL(existing.url);
        if (outcome.ok) {
          return setEntry(prev, castKey, which, {
            file,
            status: 'ready',
            url: URL.createObjectURL(outcome.blob),
          });
        }
        console.warn(
          `[heic-preview] fallback: preview failed for "${file.name}" (${file.size}B), reason=${outcome.reason}. Photo still queued for GCS upload.`,
        );
        return setEntry(prev, castKey, which, { file, status: 'heic', url: null });
      });
    },
    [],
  );

  const setSlotPhoto = useCallback(
    (castKey: string, which: 'face', file: File | null) => {
      noteSlotPhotoForAnalytics(castKey, which, file);
      void putPhotoPick(castKey, which, file).catch((err) => {
        console.warn(`[personen] IDB write (${which}) failed — continuing in-memory`, err);
      });
      const syncEntry = file === null ? null : deriveEntrySync(file);
      if (syncEntry !== undefined && syncEntry !== 'needs-heic-decode') {
        setPhotos((prev) => {
          const existing = prev.get(castKey)?.[which];
          if (existing?.url) URL.revokeObjectURL(existing.url);
          return setEntry(prev, castKey, which, syncEntry);
        });
        return;
      }
      if (syncEntry === 'needs-heic-decode') {
        setPhotos((prev) => {
          const existing = prev.get(castKey)?.[which];
          if (existing?.url) URL.revokeObjectURL(existing.url);
          return setEntry(prev, castKey, which, {
            file: file!,
            status: 'converting',
            url: null,
          });
        });
        void resolveHeicPreview(castKey, which, file!);
        return;
      }
      void (async () => {
        const isHeic = await needsHeicPreviewConversion(file!).catch(() => false);
        if (!isHeic) {
          setPhotos((prev) => {
            const existing = prev.get(castKey)?.[which];
            if (existing && existing.file !== file) return prev;
            if (existing?.url) URL.revokeObjectURL(existing.url);
            return setEntry(prev, castKey, which, {
              file: file!,
              status: 'ready',
              url: URL.createObjectURL(file!),
            });
          });
          return;
        }
        setPhotos((prev) => {
          const existing = prev.get(castKey)?.[which];
          if (existing && existing.file !== file) return prev;
          if (existing?.url) URL.revokeObjectURL(existing.url);
          return setEntry(prev, castKey, which, {
            file: file!,
            status: 'converting',
            url: null,
          });
        });
        void resolveHeicPreview(castKey, which, file!);
      })();
    },
    [resolveHeicPreview],
  );

  const setFacePhoto = useCallback(
    (castKey: string, file: File | null) => setSlotPhoto(castKey, 'face', file),
    [setSlotPhoto],
  );

  const clearPhotosFor = useCallback((castKey: string) => {
    setPhotos((prev) => {
      if (!prev.has(castKey)) return prev;
      const existing = prev.get(castKey)!;
      if (existing.face !== null && existing.face.url !== null) {
        URL.revokeObjectURL(existing.face.url);
      }
      const next = new Map(prev);
      next.delete(castKey);
      return next;
    });
    void idbClearPhotoPicksFor(castKey).catch((err) => {
      console.warn('[personen] IDB clearFor failed — continuing in-memory', err);
    });
  }, []);

  // Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — derived
  // facade over `blockNav`. Falls back to `DEFAULT_INCOMPLETE_NAV`
  // while the first mount hasn't decided yet (effect below). The
  // render-gate on `!photosHydrated || blockNav === null` prevents
  // this fallback from being visible — it is only here so the
  // handlers can read a consistent shape during the deciding frame.
  const effectiveNav: BlockNavState = blockNav ?? DEFAULT_INCOMPLETE_NAV;
  const openBlock = effectiveNav.openBlock;
  const reached = useMemo<ReadonlySet<BlockIndex>>(
    () => new Set(effectiveNav.reached),
    [effectiveNav.reached],
  );

  const markAnswered = useCallback(
    (n: BlockIndex) => {
      const current = blockNav ?? DEFAULT_INCOMPLETE_NAV;
      const reachedSet = new Set(current.reached);
      const wasNextReached = n < TERMINAL_BLOCK && reachedSet.has((n + 1) as BlockIndex);
      reachedSet.add(n);
      if (n < TERMINAL_BLOCK) reachedSet.add((n + 1) as BlockIndex);
      const nextOpen: BlockIndex | null =
        n === TERMINAL_BLOCK || wasNextReached ? null : ((n + 1) as BlockIndex);
      setBlockNav({
        reached: Array.from(reachedSet).sort() as BlockIndex[],
        openBlock: nextOpen,
      });
    },
    [blockNav, setBlockNav],
  );

  const reopen = useCallback(
    (n: BlockIndex) => {
      const current = blockNav ?? DEFAULT_INCOMPLETE_NAV;
      const reachedSet = new Set(current.reached);
      reachedSet.add(n);
      setBlockNav({
        reached: Array.from(reachedSet).sort() as BlockIndex[],
        openBlock: n,
      });
    },
    [blockNav, setBlockNav],
  );

  const blockState = (n: BlockIndex): SchrittBlockState =>
    openBlock === n ? 'active' : 'answered';

  const isReached = (n: BlockIndex): boolean => reached.has(n);

  // Reload-trap fix — derive-after-hydration effect. Runs once per
  // (photosHydrated, cast shape, face-photo set) change; writes back
  // only when the computed target differs from the current persisted
  // nav. The null-persisted path triggers the derive-fallback (rule
  // 3); the non-null path validates against the current data (rule
  // 2) and clamps to block 1 if a person is now incomplete.
  const facePhotoByKey = useMemo<ReadonlyMap<string, boolean>>(() => {
    const m = new Map<string, boolean>();
    for (const [castKey, slots] of photos) {
      m.set(castKey, slots.face !== null);
    }
    return m;
  }, [photos]);

  useEffect(() => {
    if (!photosHydrated) return;
    const allComplete = allPersonsComplete(cast, facePhotoByKey);
    const target = deriveBlockNavOnMount({ persisted: blockNav, allComplete });
    const same =
      blockNav !== null &&
      blockNav.openBlock === target.openBlock &&
      blockNav.reached.length === target.reached.length &&
      blockNav.reached.every((b, i) => b === target.reached[i]);
    if (!same) setBlockNav(target);
  }, [photosHydrated, cast, facePhotoByKey, blockNav, setBlockNav]);

  // Wrap removeCast to also clear the photos map for that entry.
  const removeCastAndClearPhotos = useCallback(
    (castKey: string) => {
      removeCast(castKey);
      clearPhotosFor(castKey);
    },
    [removeCast, clearPhotosFor],
  );

  // Block 1 summary — comma-joined names in cast order.
  const block1Summary = useMemo(() => {
    const names = cast.map((c) => c.name.trim()).filter((n) => n.length > 0);
    return names.length > 0
      ? names.join(STRINGS.book.personen.personenSummarySeparator)
      : STRINGS.book.personen.personenSummaryOhneNamen;
  }, [cast]);

  // K2 dialog binding
  const k2Entry = k2Key === null ? null : (cast.find((c) => c.key === k2Key) ?? null);
  const k2Photos = k2Key === null ? null : (photos.get(k2Key) ?? EMPTY_PHOTOS);
  const k2Zaehl =
    k2Key === null ? '' : `${cast.findIndex((c) => c.key === k2Key) + 1} von ${cast.length}`;

  // Reload-trap fix — gate blocks ONLY while `blockNav === null`.
  // That state is reached exclusively via the v2 → v3 migration (rule
  // 3: existing draft without persisted step). Fresh customers get
  // `FRESH_BLOCK_NAV = {reached:[1], openBlock:1}` from INITIAL_STATE
  // and render block 1 immediately — no gate. Current-v3 customers
  // get their persisted `blockNav` and render on first paint; the
  // post-photo-hydration effect clamps against the data (rule 2)
  // iff a photo turns out to be missing, which may briefly flash the
  // stale persisted openBlock on reload — acceptable, as rule 4's
  // "no visible flash" applies to the INITIAL DECISION (derive-
  // fallback), not to the validation-on-reload transition.
  //
  // The DreiSchritte header always renders so the layout doesn't jump.
  const navReady = blockNav !== null;

  return (
    <div className="space-y-8 py-6">
      <DreiSchritte />

      {navReady && isReached(1) && (
        <SchrittBlock
          n={1}
          title={STRINGS.book.personen.personenHeading}
          subtitle={STRINGS.book.personen.personenIntroLine}
          state={blockState(1)}
          summary={block1Summary}
          onEdit={() => reopen(1)}
          hideNumber
        >
          <PersonenFlow
            cast={cast}
            addCast={addCast}
            updateCast={updateCast}
            removeCast={removeCastAndClearPhotos}
            photos={photos}
            setFacePhoto={setFacePhoto}
            k2OpenKey={k2Key}
            onFacePickedForK2={(castKey) => setK2Key(castKey)}
            onAllCastComplete={() => markAnswered(1)}
            onFirstPersonComplete={handleFirstPersonComplete}
            onFirstPhotoStepReached={handleFirstPhotoStepReached}
            onRecipientChosen={handleRecipientChosen}
            onPersonNameEntered={handlePersonNameEntered}
            onPersonCompleted={handlePersonCompleted}
          />
        </SchrittBlock>
      )}

      {navReady && isReached(2) && (
        <GeschichtenschrittBlock
          state={blockState(2)}
          freitext={freitext}
          setFreitext={setFreitext}
          onWeiter={() => markAnswered(2)}
          onEdit={() => reopen(2)}
          cast={cast.map((c) => ({ key: c.key, name: c.name }))}
          onStoryKindChosen={handleStoryKindChosen}
          onErfundenTileChosen={handleErfundenTileChosen}
        />
      )}

      {navReady && isReached(3) && (
        <BlockTitelwunsch
          state={blockState(3)}
          value={titelwunsch}
          onChange={(text) => {
            setTitelwunsch(text);
            if (titelwunschError !== null) setTitelwunschError(null);
          }}
          onWeiter={() => markAnswered(3)}
          onEdit={() => reopen(3)}
          error={titelwunschError}
        />
      )}

      {navReady && reached.has(TERMINAL_BLOCK) && openBlock === null && (
        <div className="space-y-3 pt-4">
          <button type="button" onClick={() => setLightGateOpen(true)} className={BUTTON_GOLD}>
            {STRINGS.book.personen.weiterCta}
          </button>
          <p className="text-center text-xs text-dream-navy/70">
            {STRINGS.book.personen.beruhigungszeile}
          </p>
        </div>
      )}

      <K2ConfirmDialog
        open={k2Key !== null}
        onOpenChange={(open) => {
          if (open) return;
          // Confirm path: onConfirm already set the ref and cleared
          // k2Key. Radix may fire onOpenChange as a side-effect of
          // the controlled `open` prop flipping to false; the ref
          // makes the two paths distinguishable.
          if (k2ConfirmedRef.current) {
            k2ConfirmedRef.current = false;
            return;
          }
          // Dismiss path (X in the corner, ESC, outside click) —
          // Sebastian ruling 2026-09-24: treat exactly like "Anderes
          // Foto wählen". Discard the picked photo and return the
          // customer to Q_photo.
          const key = k2Key;
          setK2Key(null);
          if (key !== null) {
            setFacePhoto(key, null);
          }
        }}
        zaehlzeile={k2Zaehl}
        photoUrl={k2Photos?.face?.url ?? null}
        satz={buildK2SummarySentence(k2Entry)}
        onConfirm={() => {
          k2ConfirmedRef.current = true;
          setK2Key(null);
        }}
        onReplacePhoto={() => {
          const key = k2Key;
          setK2Key(null);
          if (key !== null) {
            setFacePhoto(key, null);
          }
        }}
      />

      <LightGateOverlay
        open={lightGateOpen}
        onOpenChange={setLightGateOpen}
        cast={cast}
        photos={toLightGateFileMap(photos)}
        freitext={freitext}
        sprache={sprache}
        titelwunsch={titelwunsch}
        storyKind={storyKind}
        onSuccess={(orderId) => {
          fireBookAnalytics(
            'book_light_gate_completed',
            `book_light_gate_completed_${orderId}`,
            'local',
            { book_order_id: orderId },
          );
          void clearAllPhotoPicks().catch((err) => {
            console.warn(
              '[personen] IDB clearAll after Light-Gate success failed — stale rows may hydrate on next mount',
              err,
            );
          });
          resetStore();
          navigate(`/book/${orderId}`);
        }}
        onTitelwunschRejected={(message) => {
          setTitelwunschError(message);
          reopen(3);
        }}
        onPhotoNeedsReselect={(castKey) => {
          // Sebastian ruling 2026-10-28 — clear the stale photo so
          // the Personen flow re-asks for it, then reopen Block 1
          // (Personen). The overlay closes itself before firing this
          // callback.
          setFacePhoto(castKey, null);
          reopen(1);
        }}
      />
    </div>
  );
}

/**
 * Sebastian ruling 2026-09-18 — K2 summary sentence composition.
 * Kept as an exported helper so the K2 tests + external consumers can
 * reference it. See K2ConfirmDialog for the dialog itself.
 */
export function buildK2SummarySentence(entry: CastTextEntry | null): string {
  if (entry === null) return '';
  const name = entry.name.trim();
  if (name.length === 0) return '';
  const parts: string[] = [name];
  if (entry.geschlecht === 'maennlich') {
    parts.push(STRINGS.book.personen.geschlechtMaennlich);
  } else if (entry.geschlecht === 'weiblich') {
    parts.push(STRINGS.book.personen.geschlechtWeiblich);
  }
  if (entry.ageYears !== null) {
    parts.push(`${entry.ageYears} Jahre`);
  }
  return parts.join(', ');
}

// ═══════════════════════════════════════════════════════════════════
// Photo-slot helpers
// ═══════════════════════════════════════════════════════════════════

function setEntry(
  prev: PhotoMap,
  castKey: string,
  which: 'face',
  entry: PhotoEntry | null,
): PhotoMap {
  const next = new Map(prev);
  const existing = next.get(castKey) ?? EMPTY_PHOTOS;
  next.set(castKey, { ...existing, [which]: entry });
  return next;
}

async function hydrateSlot(
  castKey: string,
  which: 'face',
  file: File | null,
  heicJobs: Array<{ castKey: string; which: 'face'; file: File }>,
): Promise<PhotoEntry | null> {
  if (file === null) return null;
  // Readability check — Sebastian ruling 2026-10-28. A File persisted
  // by a pre-materialize build (or any lazy content-URI reference that
  // survived structured-clone) can be unreadable at hydrate time. If
  // the slice-read fails, treat as missing so the builder re-asks for
  // the photo via the existing "Zeig uns …" UI; the stale IDB row is
  // purged by the caller.
  if (!(await isFileReadable(file))) {
    captureReadableFailure('hydrate', file);
    return null;
  }
  const sync = deriveEntrySync(file);
  if (sync !== undefined && sync !== 'needs-heic-decode') return sync;
  if (sync === 'needs-heic-decode') {
    heicJobs.push({ castKey, which, file });
    return { file, status: 'converting', url: null };
  }
  const isHeic = await needsHeicPreviewConversion(file).catch(() => false);
  if (isHeic) {
    heicJobs.push({ castKey, which, file });
    return { file, status: 'converting', url: null };
  }
  return { file, status: 'ready', url: URL.createObjectURL(file) };
}

function deriveEntrySync(file: File): PhotoEntry | 'needs-heic-decode' | undefined {
  const type = file.type.toLowerCase();
  if (
    type === 'image/heic' ||
    type === 'image/heif' ||
    type === 'image/heic-sequence' ||
    type === 'image/heif-sequence'
  ) {
    return 'needs-heic-decode';
  }
  if (type !== '' && type.startsWith('image/')) {
    return { file, status: 'ready', url: URL.createObjectURL(file) };
  }
  return undefined;
}

// ═══════════════════════════════════════════════════════════════════
// Block 2 — Geschichtenschritt (prototype, Sebastian GO 2026-10-08)
// is rendered inline via `GeschichtenschrittBlock` on the route body
// above; its implementation lives in
// `../components/geschichtenschritt/GeschichtenschrittBlock.tsx`.
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// Block 3 — Titelwunsch (unchanged)
// ═══════════════════════════════════════════════════════════════════

interface BlockTitelwunschProps {
  readonly state: SchrittBlockState;
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onWeiter: () => void;
  readonly onEdit: () => void;
  readonly error?: string | null;
}

function BlockTitelwunsch({
  state,
  value,
  onChange,
  onWeiter,
  onEdit,
  error,
}: BlockTitelwunschProps) {
  const summary = useMemo(() => {
    const trimmed = value.trim();
    return trimmed.length === 0 ? STRINGS.book.personen.titelwunschSummaryEmpty : `„${trimmed}"`;
  }, [value]);
  return (
    <SchrittBlock
      n={3}
      title={STRINGS.book.personen.titelwunschTitle}
      subtitle={STRINGS.book.personen.titelwunschSubtitle}
      state={state}
      summary={summary}
      onEdit={onEdit}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <input
            type="text"
            className={FIELD_CLASS}
            maxLength={TITELWUNSCH_MAX_LENGTH}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={STRINGS.book.personen.titelwunschTitle}
          />
          <CharacterCounter
            value={value.length}
            corridor={{ min: 0, max: TITELWUNSCH_MAX_LENGTH }}
          />
          <p className="text-xs text-dream-navy/70">
            {STRINGS.book.review.coverRegenTitleFieldHint}
          </p>
          {error !== undefined && error !== null && error.length > 0 && (
            <p role="alert" className="text-sm text-error">
              {error}
            </p>
          )}
        </div>
        <button type="button" onClick={onWeiter} className={BUTTON_OUTLINE_SM}>
          {STRINGS.book.personen.blockWeiterLabel}
        </button>
      </div>
    </SchrittBlock>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PhotoMap → CastPhotoFiles adapter for LightGateOverlay
// ═══════════════════════════════════════════════════════════════════

function toLightGateFileMap(photos: PhotoMap): ReadonlyMap<string, CastPhotoFiles> {
  const out = new Map<string, CastPhotoFiles>();
  for (const [key, entry] of photos.entries()) {
    out.set(key, {
      face: entry.face?.file ?? null,
    });
  }
  return out;
}

/**
 * Legacy-row probe (fix/single-photo). The IDB row shape narrowed to
 * `{castKey, face?: File}`, but structured-clone persistence keeps
 * any `fullBody` key written before the removal. We cannot type-check
 * for it, so inspect at runtime. Returning `true` triggers a one-shot
 * putPhotoPick rewrite that drops the stale key.
 */
function hasLegacyFullBody(row: unknown): boolean {
  return (
    typeof row === 'object' &&
    row !== null &&
    'fullBody' in row &&
    (row as { fullBody?: unknown }).fullBody !== undefined
  );
}
